import { useLayoutEffect, useRef } from 'react';

import { useSelector } from 'react-redux';

import { selectIsTestplane } from '../../../../../../shared/features/flags/selectors';
import { getCssCustomProperty } from '../../../../../../shared/helpers/get-css-custom-property';
import { useMicrophoneLevel } from '../../../../../../shared/hooks/use-microphone-level';

const MIC_ICON_PATH = NODE
    ? null
    : new Path2D(
          // eslint-disable-next-line max-len
          'M5 11C5 14.866 8.13401 18 12 18C15.866 18 19 14.866 19 11H21C21 15.4983 17.6999 19.2258 13.3887 19.8936L13 23H11L10.6113 19.8936C6.30011 19.2258 3 15.4983 3 11H5ZM12 2C14.7614 2 17 4.23858 17 7V11C17 13.7614 14.7614 16 12 16C9.23858 16 7 13.7614 7 11V7C7 4.23858 9.23858 2 12 2Z'
      );

const WAVE_PATH = NODE
    ? null
    : new Path2D(
          // eslint-disable-next-line max-len
          'M0 33V1L16.5031 6.19782C26.3307 9.29313 36.9488 8.70763 46.3765 4.55052C57.2854 -0.259719 69.7146 -0.259718 80.6235 4.55052C90.0512 8.70763 100.669 9.29314 110.497 6.19782L127 1V33H0Z'
      );

const WAVE_X_FROM = -85;
const WAVE_X_TO = -18;
const WAVE_X_STEP = 1.5;

const WAVE_Y_FROM = 27;
const WAVE_Y_TO = 0;

/** Разрешение иконки */
const BASE_CANVAS_SCALE = 4;
const DEFAULT_ICON_SIZE = 24;

let currentPosition = 0;
let currentLevel = 0;
let brandColor = '#33f583';
let iconColor = '#f3f4f5';
let iconSize = DEFAULT_ICON_SIZE;
let canvasScale = BASE_CANVAS_SCALE;
let canvasSize = iconSize * canvasScale;

const getLevelIncrement = (rawLevel: number, debouncedLevel: number) => {
    const difference = rawLevel - debouncedLevel;

    /**
     * для плавного изменения положения волны делим разницу между фактом и отображением на 10 и округляем до тысячных.
     */
    return Math.trunc((difference / 10) * 1000) / 1000;
};

const getWaveOffsetY = (debouncedLevel: number) => {
    /**
     * Экспонента нужна, чтобы низкие показатели громкости сильнее воздействовали на позицию волны
     * (коэффициенты подбирал руками по графику, что бы при громкости 1 была позиция -2)
     */
    const offset = WAVE_Y_FROM / Math.exp(2 * debouncedLevel) - 5;
    return offset < WAVE_Y_TO ? WAVE_Y_TO : offset;
};

/**
 * Слушает громкость микрофона и рисует на канвасе эту громкость в виде волны.
 * @returns
 */
export const useWave = () => {
    /**
     * Уровень громкости высчитывается как процент из следующего диапазона [MIN_DBFS,0]
     * Сейчас MIN_DBFS = -40 потому что это типичная бытовая тишина
     * А речь средней громкости примерно -20 DBFS
     * В таком случае логично закрашивать иконку микрофон полностью, а не на 50%
     * Поэтому используем multiFactor = 2
     */
    const microphoneLevel = useMicrophoneLevel({ multiFactor: 2 });
    const isTestplane = useSelector(selectIsTestplane);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const ctxRef = useRef<CanvasRenderingContext2D | null>(null);

    const draw = () => {
        const ctx = ctxRef.current;
        if (ctx) {
            ctx.save();
            ctx.scale(canvasScale, canvasScale);
            ctx.clearRect(0, 0, canvasSize, canvasSize);

            ctx.fillStyle = iconColor;
            if (MIC_ICON_PATH) {
                ctx.fill(MIC_ICON_PATH);
            }

            if (isTestplane) {
                ctx.restore();
                return;
            }
            /** Перемещение волны по вертикали */
            const level = microphoneLevel.current ?? 0;
            currentLevel += getLevelIncrement(level, currentLevel);
            if (currentLevel < 0) {
                currentLevel = 0;
            }
            const waveOffsetY = getWaveOffsetY(currentLevel);

            /** Отображение волны по маске иконки микрофона */
            ctx.globalCompositeOperation = 'source-atop';
            ctx.fillStyle = brandColor;

            ctx.translate(currentPosition, waveOffsetY);
            /**
             * Постоянное движение волны слева направо
             */
            currentPosition += WAVE_X_STEP;
            if (currentPosition > WAVE_X_TO) {
                currentPosition = WAVE_X_FROM;
            }
            if (WAVE_PATH) {
                ctx.fill(WAVE_PATH);
            }

            ctx.restore();
        }
    };

    useLayoutEffect(() => {
        let animationFrameId: number;

        brandColor = getCssCustomProperty('--orb-surface-brand');
        iconColor = getCssCustomProperty('--orb-color-cool-gray-50');
        iconSize = parseInt(getCssCustomProperty('--orb-size-12-m'), 10) || 24;

        canvasScale = BASE_CANVAS_SCALE * window.devicePixelRatio;
        canvasSize = iconSize * canvasScale;
        if (canvasRef.current) {
            canvasRef.current.width = canvasSize;
            canvasRef.current.height = canvasSize;
        }
        if (canvasRef.current) {
            ctxRef.current = canvasRef?.current?.getContext('2d');
        }
        const loop = () => {
            draw();
            animationFrameId = window.requestAnimationFrame(loop);
        };
        loop();
        return () => {
            if (animationFrameId) {
                window.cancelAnimationFrame(animationFrameId);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { canvasRef };
};
